import { useState, useMemo } from 'react'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { formatDistance, getStorePhotoUrl } from '@/utils/formatters'
import { MapPinIcon, WalkingIcon, ClockIcon } from '@/components/ui/icons'
import { getStorePickupStatus } from '@/utils/storePickupStatus'
import type { Store } from '@/types'

interface DesktopStoreCardProps {
  store: Store
  isSelected: boolean
  onSelect: () => void
  onHover: () => void
}

export function DesktopStoreCard({ store, isSelected, onSelect, onHover }: DesktopStoreCardProps) {
  const photoUrl = getStorePhotoUrl(store.storePhotoPath, store.logoPath)
  const [imgError, setImgError] = useState(false)
  const pickupStatus = useMemo(() => getStorePickupStatus(store), [store])

  return (
    <div
      data-store-id={store.storeId}
      className={`
        p-4 border-b border-border-light
        cursor-pointer transition-all duration-200
        ${isSelected 
          ? 'bg-brand-primary/5 border-l-4 border-l-brand-primary' 
          : 'hover:bg-surface-secondary'
        }
      `}
      onClick={onSelect}
      onMouseEnter={onHover}
      role="option"
      aria-selected={isSelected}
    >
      <div className="flex items-start gap-3">
        <div className="w-16 h-16 rounded-lg overflow-hidden bg-surface-tertiary shrink-0">
          {photoUrl && !imgError ? (
            <img 
              src={photoUrl} 
              alt={`${store.name} location`}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={() => setImgError(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <MapPinIcon className="size-6 text-text-tertiary" />
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-text-primary truncate">
                {store.name}
              </h3>
            </div>
            
            <StatusBadge status={pickupStatus.status} size="sm" />
          </div>

          <p className="text-sm/5 text-text-secondary truncate mt-1">
            {store.street}, {store.area}
          </p>

          <div className="flex items-center gap-3 mt-2 text-sm/5 text-text-secondary">
            {store.distance !== undefined && (
              <span className="flex items-center gap-1">
                <MapPinIcon className="size-4 text-text-tertiary" />
                {formatDistance(store.distance)}
              </span>
            )}
            {store.travelTime !== undefined && (
              <span className="flex items-center gap-1">
                <WalkingIcon className="size-4 text-text-tertiary" />
                {store.travelTime} min
              </span>
            )}
            {pickupStatus.closingTime && (
              <span className="flex items-center gap-1">
                <ClockIcon className="size-4 text-text-tertiary" />
                {pickupStatus.status === 'closing_soon'
                  ? `Closes at ${pickupStatus.closingTime}`
                  : `Open till ${pickupStatus.closingTime}`}
              </span>
            )}
          </div>

          {!pickupStatus.isAvailableForPickup && pickupStatus.nextOpenTime && (
            <p className="mt-1.5 text-sm/5 text-text-tertiary">
              {pickupStatus.nextOpenTime}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
